import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Phone, Stethoscope, Video, CalendarCheck, HeartPulse, Baby, ShieldCheck, ArrowRight,
  Users, Wallet, FlaskConical, MapPin, MessageCircle, FileText, Activity, ClipboardCheck
} from 'lucide-react';
import { PHONE_DISPLAY, PHONE_TEL, PORTAL_URL } from '../SiteLayout';
import InstallAppButton from '../../components/InstallAppButton';
import PageSEO from '../../components/PageSEO';
import { resolveArticleImage } from '../utils/articleUtils';
import { CMT_PHOTOS, photoThumb } from '../data/photos';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Chiffres clés officiels — validés par le Directeur Général (Content Book 2.5)
const KEY_FIGURES = [
  { value: '2015', label: 'Année de création' },
  { value: '2 sites', label: 'Matadi & Boma' },
  { value: '45', label: 'Professionnels' },
  { value: '6 000', label: 'Consultations par an' },
  { value: '250', label: 'Accouchements par an' },
  { value: '300', label: 'Interventions chirurgicales par an' },
  { value: '30 000', label: 'Examens de laboratoire par an' },
];

const PATIENT_JOURNEY = [
  { icon: <CalendarCheck size={20} />, title: 'Choisissez', text: 'Votre spécialité ou votre médecin' },
  { icon: <Wallet size={20} />, title: 'Réservez', text: 'En ligne ou par téléphone' },
  { icon: <MessageCircle size={20} />, title: 'Confirmation', text: 'Instantanée par WhatsApp' },
  { icon: <Stethoscope size={20} />, title: 'Consultation', text: 'Au centre ou en vidéo' },
  { icon: <FlaskConical size={20} />, title: 'Examens', text: 'Laboratoire & imagerie si nécessaire' },
  { icon: <FileText size={20} />, title: 'Documents', text: "Dans votre espace patient" },
];

const WHY_CMT = [
  { icon: <Users size={26} />, title: 'Une équipe de médecins spécialistes', text: "Nos médecins et professionnels de santé disposent de compétences reconnues dans plusieurs disciplines afin d'assurer une prise en charge adaptée à chaque patient." },
  { icon: <ClipboardCheck size={26} />, title: 'Une médecine centrée sur le patient', text: "Chaque patient bénéficie d'une écoute attentive, d'une information claire et d'un accompagnement personnalisé tout au long de son parcours de soins." },
  { icon: <FlaskConical size={26} />, title: 'Des équipements modernes', text: "Le Centre dispose de services de laboratoire, d'imagerie médicale, de chirurgie, de maternité et d'autres infrastructures permettant une prise en charge coordonnée." },
  { icon: <Video size={26} />, title: 'La téléconsultation', text: "Grâce aux outils numériques, les patients peuvent consulter un médecin à distance lorsque leur situation le permet — y compris des spécialistes exerçant dans d'autres provinces ou à l'étranger." },
  { icon: <Activity size={26} />, title: 'Des solutions numériques innovantes', text: "Le portail patient permet de prendre rendez-vous, recevoir des notifications, accéder à certains documents médicaux et bénéficier progressivement de nouveaux services numériques." },
  { icon: <ShieldCheck size={26} />, title: 'Une relation de confiance', text: "Depuis sa création, le Centre Médical la Trinité construit une relation durable avec ses patients fondée sur l'éthique, la transparence et la qualité des soins." },
];

export default function SiteHome() {
  const [doctors, setDoctors] = useState([]);
  const [articles, setArticles] = useState([]);

  useEffect(() => {
    fetch(`${API_URL}/api/doctors`)
      .then(r => r.json())
      .then(d => setDoctors((d.doctors || []).filter(doc => !doc.name.toLowerCase().includes('test')).slice(0, 3)))
      .catch(() => {});
    fetch(`${API_URL}/api/journal`)
      .then(r => r.json())
      .then(d => setArticles((d.articles || []).slice(0, 3)))
      .catch(() => {});
  }, []);

  return (
    <main data-testid="site-home">
      <PageSEO
        title="Rendez-vous en ligne à Matadi & Boma"
        description="Centre Médical la Trinité — cabinet médical de référence à Matadi & Boma (RDC). Consultations, téléconsultation, chirurgie, gynécologie, pédiatrie. Prenez rendez-vous en ligne 24h/24."
        path="/"
      />
      <section
        className="site-hero"
        style={{
          backgroundImage: "url('/photos/accueil_equipe.jpg')",
          backgroundPosition: '50% 30%',
        }}
      >
        <div className="site-hero-content">
          <div className="site-hero-badge site-reveal">
            <ShieldCheck size={15} /> Centre médical certifié · Matadi & Boma
          </div>
          <h1 className="site-reveal-1">
            L'excellence médicale au service des <em>familles</em>
          </h1>
          <p className="site-reveal-2">
            Depuis 2015, le Centre Médical la Trinité accompagne les femmes, les enfants,
            les familles et les entreprises grâce à une médecine spécialisée, humaine et
            innovante. Notre équipe met son expertise au service de votre santé afin de
            vous offrir des soins de qualité dans un environnement moderne, sécurisé et
            centré sur le patient.
          </p>
          <div className="site-hero-actions site-reveal-3">
            <a href={`${PORTAL_URL}?page=booking`} className="site-btn site-btn-primary" data-testid="hero-booking-btn">
              <CalendarCheck size={18} /> Prendre rendez-vous
            </a>
            <Link to="/teleconsultation" className="site-btn site-btn-outline" data-testid="hero-tele-btn">
              <Video size={18} /> Téléconsultation
            </Link>
            <InstallAppButton className="site-btn site-btn-outline" label="Télécharger l'app" />
          </div>
        </div>
      </section>

      <div className="site-urgence" data-testid="site-urgence-banner">
        <div className="site-urgence-inner">
          <span>Urgence médicale ? Notre équipe répond 24h/24 et 7j/7</span>
          <a href={`tel:${PHONE_TEL}`} data-testid="urgence-call-btn">
            <Phone size={16} /> {PHONE_DISPLAY}
          </a>
        </div>
      </div>

      {/* Chiffres clés */}
      <section className="site-section" style={{ paddingBottom: '2.5rem' }}>
        <div className="site-figures" data-testid="site-key-figures">
          {KEY_FIGURES.filter(f => f.value).map((f, i) => (
            <div className="site-figure" key={i}>
              <strong>{f.value}</strong>
              <span>{f.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Pourquoi choisir le CMT */}
      <div className="site-section-alt-wrap">
        <section className="site-section">
          <span className="site-kicker">Nos atouts</span>
          <h2>Pourquoi choisir le Centre Médical la Trinité ?</h2>
          <p className="site-section-sub">
            Six raisons qui font du CMT le partenaire santé de confiance de votre famille.
          </p>
          <div className="site-grid" data-testid="site-why-cmt">
            {WHY_CMT.map((s, i) => (
              <div className="site-card" key={i}>
                <div className="site-card-icon">{s.icon}</div>
                <h3>{s.title}</h3>
                <p>{s.text}</p>
              </div>
            ))}
          </div>
        </section>
      </div>

      {/* Parcours patient */}
      <section className="site-section">
        <span className="site-kicker">Votre parcours</span>
        <h2>Un parcours patient simple, de A à Z</h2>
        <p className="site-section-sub">
          Du premier clic jusqu'au suivi de votre santé, tout est pensé pour vous simplifier la vie.
        </p>
        <div className="site-journey" data-testid="site-patient-journey">
          {PATIENT_JOURNEY.map((s, i) => (
            <div className="site-journey-step" key={i}>
              <div className="site-journey-icon">{s.icon}<span className="site-journey-num">{i + 1}</span></div>
              <h3>{s.title}</h3>
              <p>{s.text}</p>
            </div>
          ))}
        </div>
        <div style={{ marginTop: '2.2rem' }}>
          <a href={`${PORTAL_URL}?page=booking`} className="site-btn site-btn-primary" data-testid="journey-booking-btn">
            <CalendarCheck size={18} /> Commencer mon parcours
          </a>
        </div>
      </section>

      {/* Équipe */}
      {doctors.length > 0 && (
        <div className="site-section-alt-wrap">
          <section className="site-section">
            <span className="site-kicker">Notre équipe</span>
            <h2>Des médecins expérimentés et à votre écoute</h2>
            <p className="site-section-sub">Généralistes et spécialistes engagés pour votre santé.</p>
            <div className="site-grid">
              {doctors.map(doc => {
                const isPlaceholder = !doc.photo_url || /via\.placeholder|placehold\.co|cmt_logo/i.test(doc.photo_url);
                return (
                <div className="site-doctor-card" key={doc.doctor_id}>
                  <img
                    src={isPlaceholder ? '/doctor-placeholder.svg' : doc.photo_url}
                    alt={doc.name}
                    loading="lazy"
                    onError={(e) => { e.target.onerror = null; e.target.src = '/doctor-placeholder.svg'; }}
                  />
                  <div className="site-doctor-body">
                    <h3>{doc.name}</h3>
                    <div className="site-doctor-spec">{doc.specialty}</div>
                    <p>{doc.bio}</p>
                  </div>
                </div>
                );
              })}
            </div>
            <div style={{ marginTop: '2rem' }}>
              <Link to="/medecins" className="site-btn site-btn-outline" data-testid="home-all-doctors-btn">
                Toute l'équipe médicale <ArrowRight size={16} />
              </Link>
            </div>
          </section>
        </div>
      )}

      {/* Notre centre en images */}
      <div className="site-section-alt-wrap">
        <section className="site-section">
          <span className="site-kicker">Notre centre en images</span>
          <h2>Découvrez le Centre Médical la Trinité</h2>
          <p className="site-section-sub">
            Des locaux modernes, un environnement apaisant, une équipe engagée à votre service.
          </p>
          <div className="cmt-home-gallery" data-testid="site-home-gallery-preview">
            {CMT_PHOTOS.slice(0, 4).map((p) => (
              <Link to="/galerie" key={p.file} aria-label={p.caption} data-testid={`home-gallery-${p.file}`}>
                <img src={photoThumb(p.file)} alt={p.caption} loading="lazy" />
              </Link>
            ))}
          </div>
          <div style={{ marginTop: '2rem' }}>
            <Link to="/galerie" className="site-btn site-btn-outline" data-testid="home-gallery-btn">
              Voir toute la galerie <ArrowRight size={16} />
            </Link>
          </div>
        </section>
      </div>

      {/* Journal Médical */}
      {articles.length > 0 && (
        <section className="site-section">
          <span className="site-kicker">Journal Médical CMT</span>
          <h2>S'informer pour mieux se soigner</h2>
          <p className="site-section-sub">Conseils santé, prévention et actualités du centre, rédigés par notre équipe médicale.</p>
          <div className="site-grid" data-testid="site-home-journal">
            {articles.map(a => {
              const cover = resolveArticleImage(a.image_url);
              return (
                <Link to={`/journal/${a.slug}`} className="site-card site-article-card" key={a.article_id} data-testid={`home-article-${a.slug}`}>
                  {cover && (
                    <div className="site-article-thumb">
                      <img src={cover} alt={a.title} onError={(e) => { e.currentTarget.parentElement.style.display = 'none'; }} />
                    </div>
                  )}
                  <span className="site-article-cat">{a.category}</span>
                  <h3>{a.title}</h3>
                  <p>{a.excerpt}</p>
                  <span className="site-article-more">Lire l'article <ArrowRight size={14} /></span>
                </Link>
              );
            })}
          </div>
          <div style={{ marginTop: '2rem' }}>
            <Link to="/journal" className="site-btn site-btn-outline" data-testid="home-all-articles-btn">
              Tout le journal <ArrowRight size={16} />
            </Link>
          </div>
        </section>
      )}

      <section className="site-section">
        <div className="site-cta-band">
          <div>
            <h2>Prêt à prendre soin de votre santé ?</h2>
            <p>Prenez rendez-vous en moins de 3 minutes depuis votre téléphone, ou consultez un médecin en vidéo dès aujourd'hui.</p>
          </div>
          <a href={`${PORTAL_URL}?page=booking`} className="site-btn site-btn-outline" style={{ background: 'white' }} data-testid="home-cta-booking-btn">
            <CalendarCheck size={18} /> Prendre rendez-vous
          </a>
        </div>
      </section>
    </main>
  );
}
